import { fetchLandingEventStats } from '@/api/landings';
import type { StatsPeriod } from '@/entities/admin-stats.entity';
import type { ILandingEventStats } from '@/entities/landing.entity';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import { useCallback, useEffect, useState } from 'react';
import LandingEventsStatsView from './landing-events-stats-view';
import LandingEventsSkeleton, { LandingEventsDataSkeleton } from './landing-events-skeleton';

interface LandingEventsStatsTabProps {
	period: StatsPeriod;
	linkId: string;
}

export default function LandingEventsStatsTab({ period, linkId }: LandingEventsStatsTabProps) {
	const queryKey = `${period}:${linkId}`;
	const [stats, setStats] = useState<ILandingEventStats | null>(null);
	const [error, setError] = useState(false);
	const [resolvedKey, setResolvedKey] = useState<string | null>(null);
	const loading = resolvedKey !== queryKey;
	const displayStats = resolvedKey === queryKey ? stats : null;

	useEffect(() => {
		let cancelled = false;

		fetchLandingEventStats({ period, linkId: linkId || undefined })
			.then(data => {
				if (!cancelled) {
					setStats(data);
					setError(false);
					setResolvedKey(queryKey);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setStats(null);
					setError(true);
					setResolvedKey(queryKey);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [period, linkId, queryKey]);

	const retryLoad = useCallback(() => {
		setResolvedKey(null);
		setError(false);
		fetchLandingEventStats({ period, linkId: linkId || undefined })
			.then(data => {
				setStats(data);
				setResolvedKey(queryKey);
			})
			.catch(() => {
				setStats(null);
				setError(true);
				setResolvedKey(queryKey);
			});
	}, [period, linkId, queryKey]);

	if (loading && !displayStats) {
		return <LandingEventsSkeleton showFilters={false} />;
	}

	if (error && resolvedKey === queryKey) {
		return (
			<LandingsErrorState
				message='Не удалось загрузить статистику. Проверьте подключение и повторите.'
				onRetry={retryLoad}
			/>
		);
	}

	if (!displayStats) {
		return <LandingEventsSkeleton showFilters={false} />;
	}

	return (
		<div className='flex flex-col gap-4'>
			{loading ? <LandingEventsDataSkeleton /> : <LandingEventsStatsView stats={displayStats} />}
		</div>
	);
}
